import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  ClipboardCheck, Plus, Calendar, CheckCircle2, Circle, 
  AlertTriangle, FileText, Shield, Stethoscope, GraduationCap,
  ChevronRight, Trash2, RefreshCw
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  documentation: { label: 'Documentation', icon: FileText, color: 'text-blue-500' },
  clinical: { label: 'Clinical', icon: Stethoscope, color: 'text-emerald-500' },
  compliance: { label: 'Compliance', icon: Shield, color: 'text-purple-500' },
  safety: { label: 'Safety', icon: AlertTriangle, color: 'text-orange-500' },
  training: { label: 'Training', icon: GraduationCap, color: 'text-cyan-500' }
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-500' },
  high: { label: 'High', color: 'bg-orange-500' },
  medium: { label: 'Medium', color: 'bg-yellow-500' },
  low: { label: 'Low', color: 'bg-blue-500' }
};

const ProgressRing = ({ progress, size = 120, strokeWidth = 10 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  const getColor = (p) => {
    if (p >= 100) return '#10B981';
    if (p >= 80) return '#22D3EE';
    if (p >= 50) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted/20"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor(progress)}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color: getColor(progress) }}>
          {Math.round(progress)}%
        </span>
        <span className="text-xs text-muted-foreground">Complete</span>
      </div>
    </div>
  );
};

const ChecklistItem = ({ item, onToggle, onUpdateNotes }) => {
  const [notes, setNotes] = useState(item.notes || '');
  const [showNotes, setShowNotes] = useState(false);
  const CategoryIcon = CATEGORY_CONFIG[item.category]?.icon || FileText;
  const priorityConfig = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;

  return (
    <div className={`checklist-item ${item.is_complete ? 'completed' : ''}`}>
      <Checkbox
        checked={item.is_complete}
        onCheckedChange={(checked) => onToggle(item.id, checked)}
        className="mt-1"
        data-testid={`checkbox-${item.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <CategoryIcon className={`h-4 w-4 ${CATEGORY_CONFIG[item.category]?.color}`} />
          <span className={`font-medium ${item.is_complete ? 'line-through text-muted-foreground' : ''}`}>
            {item.item}
          </span>
          <div className={`h-2 w-2 rounded-full ${priorityConfig.color}`} title={priorityConfig.label} />
        </div>
        {item.description && (
          <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
        )}
        {item.is_complete && item.completed_by && (
          <p className="text-xs text-muted-foreground">
            Completed by {item.completed_by} on {new Date(item.completed_at).toLocaleDateString()}
          </p>
        )}
        {showNotes && (
          <div className="mt-3 space-y-2">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes..."
              className="min-h-[60px] text-sm"
            />
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                onUpdateNotes(item.id, notes);
                setShowNotes(false);
              }}
            >
              Save Notes
            </Button>
          </div>
        )}
      </div>
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => setShowNotes(!showNotes)}
        className="shrink-0"
      >
        <ChevronRight className={`h-4 w-4 transition-transform ${showNotes ? 'rotate-90' : ''}`} />
      </Button>
    </div>
  );
};

export default function SurveyReadiness() {
  const { token } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  
  const [newSurvey, setNewSurvey] = useState({
    survey_type: 'CMS',
    target_date: ''
  });

  const fetchSurveys = async () => {
    try {
      const response = await fetch(`${API_URL}/api/survey-readiness`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSurveys(data);
        if (data.length > 0 && !activeSurvey) {
          setActiveSurvey(data[0]);
        }
      }
    } catch (error) {
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, [token]);

  const createSurvey = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/survey-readiness?survey_type=${newSurvey.survey_type}&target_date=${newSurvey.target_date}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success('Survey checklist created');
        setSurveys([data, ...surveys]);
        setActiveSurvey(data);
        setCreateDialogOpen(false);
      }
    } catch (error) {
      toast.error('Failed to create survey');
    }
  };

  const toggleItem = async (itemId, isComplete) => {
    if (!activeSurvey) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/survey-readiness/${activeSurvey.id}/item/${itemId}`,
        {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ is_complete: isComplete })
        }
      );
      if (response.ok) {
        const result = await response.json();
        // Update local state
        setActiveSurvey(prev => ({
          ...prev,
          overall_score: result.overall_score,
          status: result.status,
          checklist: (prev.checklist || prev.checklist_items || []).map(item =>
            item.id === itemId ? { ...item, is_complete: isComplete } : item
          )
        }));
      }
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const updateNotes = async (itemId, notes) => {
    if (!activeSurvey) return;
    
    const items = activeSurvey.checklist || activeSurvey.checklist_items || [];
    const item = items.find(i => i.id === itemId);
    try {
      const response = await fetch(
        `${API_URL}/api/survey-readiness/${activeSurvey.id}/item/${itemId}?is_complete=${item.is_complete}&notes=${encodeURIComponent(notes)}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        setActiveSurvey(prev => ({
          ...prev,
          checklist: (prev.checklist || prev.checklist_items || []).map(i =>
            i.id === itemId ? { ...i, notes } : i
          )
        }));
        toast.success('Notes saved');
      }
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const deleteSurvey = async (surveyId) => {
    if (!window.confirm('Delete this survey checklist?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/survey-readiness/${surveyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setSurveys(surveys.filter(s => s.id !== surveyId));
        if (activeSurvey?.id === surveyId) {
          setActiveSurvey(surveys.length > 1 ? surveys.find(s => s.id !== surveyId) : null);
        }
        toast.success('Survey deleted');
      }
    } catch (error) {
      toast.error('Failed to delete survey');
    }
  };

  const getFilteredItems = () => {
    if (!activeSurvey) return [];
    const items = activeSurvey.checklist || activeSurvey.checklist_items || [];
    if (activeCategory === 'all') return items;
    return items.filter(item => item.category === activeCategory);
  };

  const getCategoryStats = () => {
    if (!activeSurvey) return {};
    const items = activeSurvey.checklist || activeSurvey.checklist_items || [];
    const stats = {};
    Object.keys(CATEGORY_CONFIG).forEach(catKey => {
      const catItems = items.filter(i => i.category === catKey);
      const completed = catItems.filter(i => i.is_complete).length;
      stats[catKey] = { total: catItems.length, completed };
    });
    return stats;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="survey-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 lg:col-span-3" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="survey-readiness-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <ClipboardCheck className="h-8 w-8 text-primary" />
            Survey Readiness
          </h1>
          <p className="text-muted-foreground mt-1">Prepare for CMS, CHAP, and State surveys</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-survey-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Checklist
        </Button>
      </div>

      {surveys.length === 0 ? (
        <Card className="py-16">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-teal-500/20 flex items-center justify-center mb-6">
              <ClipboardCheck className="h-10 w-10 text-primary" />
            </div>
            <h3 className="font-semibold text-xl mb-2">No Survey Checklists</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Create a survey readiness checklist to track your compliance status before CMS, CHAP, or State surveys.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Checklist
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Survey Selection & Progress */}
          <div className="space-y-4">
            {/* Progress Card */}
            {activeSurvey && (
              <Card className="overflow-hidden">
                <div className="hero-gradient p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="secondary" className="bg-white/20 text-white border-0">
                      {activeSurvey.survey_type}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-white/70 hover:text-white hover:bg-white/20"
                      onClick={() => deleteSurvey(activeSurvey.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex justify-center">
                    <ProgressRing progress={activeSurvey.overall_score || 0} />
                  </div>
                  {activeSurvey.target_date && (
                    <div className="mt-4 text-center text-white/80 text-sm">
                      <Calendar className="h-4 w-4 inline mr-1" />
                      Target: {new Date(activeSurvey.target_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="text-center">
                    <Badge variant={activeSurvey.overall_score >= 100 ? 'default' : 'secondary'} className={
                      activeSurvey.overall_score >= 100 ? 'bg-emerald-500' : 
                      activeSurvey.overall_score >= 80 ? 'bg-cyan-500' : ''
                    }>
                      {activeSurvey.overall_score >= 100 ? 'Survey Ready' :
                       activeSurvey.overall_score >= 80 ? 'Almost Ready' : 'In Progress'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Survey List */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Checklists</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-48">
                  {surveys.map(survey => (
                    <button
                      key={survey.id}
                      onClick={() => setActiveSurvey(survey)}
                      className={`w-full p-3 text-left hover:bg-muted transition-colors border-l-4 ${
                        activeSurvey?.id === survey.id ? 'border-primary bg-primary/5' : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{survey.survey_type}</span>
                        <span className="text-xs text-muted-foreground">{Math.round(survey.overall_score)}%</span>
                      </div>
                      <Progress value={survey.overall_score} className="h-1 mt-2" />
                    </button>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Category Stats */}
            {activeSurvey && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">By Category</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(getCategoryStats()).map(([cat, stats]) => {
                    const config = CATEGORY_CONFIG[cat];
                    const Icon = config?.icon || FileText;
                    const percent = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;
                    return (
                      <div key={cat} className="flex items-center gap-3">
                        <Icon className={`h-4 w-4 ${config?.color}`} />
                        <div className="flex-1">
                          <div className="flex justify-between text-xs mb-1">
                            <span>{config?.label}</span>
                            <span>{stats.completed}/{stats.total}</span>
                          </div>
                          <Progress value={percent} className="h-1.5" />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content - Checklist */}
          <div className="lg:col-span-3">
            {activeSurvey && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{activeSurvey.survey_type} Survey Checklist</CardTitle>
                      <CardDescription>
                        {(activeSurvey.checklist || activeSurvey.checklist_items || []).filter(i => i.is_complete).length} of {(activeSurvey.checklist || activeSurvey.checklist_items || []).length} items complete
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchSurveys}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeCategory} onValueChange={setActiveCategory}>
                    <TabsList className="mb-6">
                      <TabsTrigger value="all">All</TabsTrigger>
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <TabsTrigger key={key} value={key} className="gap-2">
                          <config.icon className="h-4 w-4" />
                          <span className="hidden sm:inline">{config.label}</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-3">
                        {getFilteredItems().map((item, index) => (
                          <div
                            key={item.id}
                            className="animate-fade-in"
                            style={{ animationDelay: `${index * 30}ms` }}
                          >
                            <ChecklistItem
                              item={item}
                              onToggle={toggleItem}
                              onUpdateNotes={updateNotes}
                            />
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Survey Checklist</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Survey Type</Label>
              <Select 
                value={newSurvey.survey_type} 
                onValueChange={(v) => setNewSurvey({ ...newSurvey, survey_type: v })}
              >
                <SelectTrigger data-testid="survey-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CMS">CMS Survey</SelectItem>
                  <SelectItem value="CHAP">CHAP Accreditation</SelectItem>
                  <SelectItem value="State">State Survey</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target Date (Optional)</Label>
              <Input
                type="date"
                value={newSurvey.target_date}
                onChange={(e) => setNewSurvey({ ...newSurvey, target_date: e.target.value })}
                data-testid="target-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={createSurvey} data-testid="create-survey-submit">Create Checklist</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
